'use client';

import classNames from 'classnames';
import { useMemo, useState } from 'react';
import Image from '@alltrails/core/components/Image';
import Remove from '@alltrails/denali/icons/Remove';
import type { InternalCardProps } from '../../types';
import CardLink from '../CardLink';
import CompletionBadge from '../CompletionBadge';
import HeartButton from '../HeartButton';
import PhotoCarousel from '../PhotoCarousel';
import styles from './styles/styles.module.scss';

export type StandardCardProps = InternalCardProps & {
  photos: (string | { url: string; alt: string })[];
  attributionText?: string;
  pinnedPhoto?: string;
  loadPhotos?: () => void;
  squarePhoto?: boolean;
  role?: string;
  ariaLabel?: string;
  isLoadingPhotos?: boolean;
};

const StandardCard = ({
  attributionText,
  className,
  name,
  href,
  openInNewTab,
  onCardClick,
  onCardMouseEnter,
  onCardMouseLeave,
  metadata,
  onFavoriteClick,
  isFavorite = false,
  isCompleted = false,
  isVerified = false,
  deleteButton,
  photos,
  pinnedPhoto,
  loadPhotos,
  squarePhoto,
  onCompletionBadgeClick,
  blockNavigation,
  lazyLoadImages,
  role,
  ariaLabel,
  isLoadingPhotos
}: StandardCardProps) => {
  const editMode = !!deleteButton;
  const [cardHovered, setCardHovered] = useState(false);
  const cardOnMouseEnter = () => {
    setCardHovered(true);
    onCardMouseEnter?.();
  };
  const cardOnMouseLeave = () => {
    setCardHovered(false);
    onCardMouseLeave?.();
  };

  const imgLoading = lazyLoadImages ? 'lazy' : 'eager';

  const leftOverlayElement = useMemo(() => {
    if (attributionText) {
      return <div className={styles.attributionContainer}>{attributionText}</div>;
    }

    return <CompletionBadge isCompleted={isCompleted} isVerified={isVerified} onClick={onCompletionBadgeClick} />;
  }, [attributionText, isCompleted, isVerified, onCompletionBadgeClick]);

  const rightOverlayElement = useMemo(() => {
    if (deleteButton) {
      return (
        <button
          className={styles.deleteButton}
          onClick={deleteButton.onClick}
          aria-label={deleteButton.title}
          title={deleteButton.title}
          type="button"
        >
          <Remove color="--color-neutral-white" />
        </button>
      );
    }
    return onFavoriteClick ? <HeartButton isSelected={isFavorite} onClick={onFavoriteClick} /> : null;
  }, [deleteButton, isFavorite, onFavoriteClick]);

  return (
    <div
      className={classNames(styles.container, className)}
      onMouseEnter={cardOnMouseEnter}
      onMouseLeave={cardOnMouseLeave}
      role={role}
      aria-label={ariaLabel}
    >
      <CardLink href={href} blockNavigation={blockNavigation} openInNewTab={openInNewTab} onClick={onCardClick} isOverlay overlayFocusStyle="offset">
        {name}
      </CardLink>
      <div className={styles.carousel}>
        <CardLink ariaLabel={name} href={href} blockNavigation={blockNavigation} openInNewTab={openInNewTab} onClick={onCardClick} preventFocus>
          <PhotoCarousel
            photos={editMode && photos.length ? [photos[0]] : photos}
            loadPhotos={loadPhotos}
            zoomPhoto={editMode ? false : cardHovered}
            uniqueId={name}
            aspectRatio={squarePhoto ? 'square' : 'wide'}
            lazyLoadImages={lazyLoadImages}
            isLoading={isLoadingPhotos}
          />
          {pinnedPhoto ? (
            <Image src={pinnedPhoto} alt="" className={styles.pinnedPhoto} loading={imgLoading} unoptimized width={0} height={0} />
          ) : null}
        </CardLink>
        {editMode ? <div className={styles.nonInteractiveOverlay} /> : null}
      </div>
      <div className={styles.overlays}>
        {leftOverlayElement}
        {rightOverlayElement}
      </div>
      <div className={styles.metadata}>{metadata}</div>
    </div>
  );
};

export default StandardCard;
